import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Layers, Loader2, Search } from "lucide-react";
import { toast } from "sonner";

const formatCOP = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const CAUSA = {
  posible_duplicado: { label: "Posible duplicado", cls: "bg-rose-100 text-rose-800" },
  costo_distinto: { label: "Costo distinto", cls: "bg-amber-100 text-amber-800" },
  fusion: { label: "Fusión de SKUs", cls: "bg-sky-100 text-sky-800" },
};

/** Configuración → Sistema: productos con 2+ lotes del mismo contenedor (solo lectura, explica la causa). */
export default function LotesRepetidosPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const analizar = async () => {
    setLoading(true);
    try { const { data } = await api.get("/productos/_/lotes-repetidos"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo analizar"); }
    finally { setLoading(false); }
  };

  return (
    <Card className="p-5" data-testid="lotes-repetidos-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-2"><Layers className="w-4 h-4 text-primary" /> Lotes repetidos por contenedor</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Productos que recibieron <b>dos o más lotes del mismo contenedor</b>. Explica de dónde salió cada lote (SKU original del Excel si fue una fusión por familia + color). Solo muestra, no cambia nada.
          </p>
        </div>
        <Button onClick={analizar} disabled={loading} variant="outline" className="h-10" data-testid="lotes-repetidos-analizar-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} {data ? "Volver a analizar" : "Analizar"}
        </Button>
      </div>
      {data && (
        <div className="mt-4 space-y-3">
          <div className="text-xs text-muted-foreground" data-testid="lotes-repetidos-resumen">
            {data.total} producto(s) · {data.por_causa.posible_duplicado} posible duplicado · {data.por_causa.costo_distinto} costo distinto · {data.por_causa.fusion} fusión de SKUs
          </div>
          {data.total === 0 && <div className="text-sm text-emerald-700" data-testid="lotes-repetidos-vacio">Ningún producto tiene lotes repetidos del mismo contenedor.</div>}
          {data.filas.map((r, i) => <Fila key={`${r.producto_id}-${r.contenedor_id}`} r={r} i={i} />)}
        </div>
      )}
    </Card>
  );
}

function Fila({ r, i }) {
  const c = CAUSA[r.causa];
  return (
    <div className="rounded-md border border-border p-3 text-xs" data-testid={`lotes-repetidos-fila-${i}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-1.5 py-0.5 rounded font-semibold ${c.cls}`}>{c.label}</span>
        <Link to={`/productos?q=${encodeURIComponent(r.codigo || r.sku || r.producto || "")}`} className="font-medium hover:underline">{r.producto}</Link>
        <span className="text-muted-foreground">{r.codigo || r.sku}{r.archivado ? " · archivado" : ""} · contenedor <b>{r.contenedor_id}</b> · stock ficha {r.stock_actual.toLocaleString("es-CO")}</span>
      </div>
      <div className="text-muted-foreground mt-1">{r.explicacion}</div>
      <table className="w-full mt-2 tabular-nums">
        <thead className="text-muted-foreground"><tr><th className="text-left p-1">Lote</th><th className="text-left p-1">Fecha</th><th className="text-right p-1">Recibido</th><th className="text-right p-1">Disponible</th><th className="text-right p-1">Costo unit.</th><th className="text-left p-1">Origen en el Excel</th></tr></thead>
        <tbody>
          {r.lotes.map((l, j) => (
            <tr key={l.lote_id} className="border-t border-border/40">
              <td className="p-1">{j + 1}</td>
              <td className="p-1">{l.fecha || "—"}</td>
              <td className="p-1 text-right">{l.cantidad_recibida}</td>
              <td className="p-1 text-right">{l.cantidad_disponible}</td>
              <td className="p-1 text-right">{formatCOP(l.costo_unitario)}</td>
              <td className="p-1">{l.fusion_origen ? <>SKU <b>{l.fusion_origen.sku}</b>{l.fusion_origen.codigo ? ` · ${l.fusion_origen.codigo}` : ""}{l.fusion_origen.nombre ? ` · ${l.fusion_origen.nombre}` : ""}</> : <span className="text-muted-foreground">fila propia del producto{l.codigo ? ` · ${l.codigo}` : ""}</span>}</td>
            </tr>
          ))}
          <tr className="border-t border-border font-semibold"><td className="p-1" colSpan={2}>Total</td><td className="p-1 text-right">{r.total_recibido}</td><td colSpan={3}></td></tr>
        </tbody>
      </table>
    </div>
  );
}
